<template>
  <section class="file-selector">
    <div v-if="isOpen"
         class="file-selector__content">
      <div class="file-selector__header">
        <h3 class="file-selector__title">{{ $gettext('Attach document') }}</h3>
      </div>
      <div class="file-selector__tabs">
        <button v-for="tab in allTabs"
                :key="tab.tab"
                :class="{'is-active': tab.id === currentTab}"
                class="tab"
                type="button"
                @click="tab.onClick ? tab.onClick(tab.id) : ''">{{ tab.title }}</button>
      </div>
      <div class="is-progress-bar"
           :class="{'in-progress': progressActive}">
        <!----  Existing Files List ---->
        <div v-if="isDocumentsTab"
             class="file-selector__search-wrapper">
          <sk-input :placeholder="searchPlaceholder"
                    :preselected-value="searchKey"
                    :input-icon="'search'"
                    :reg-exp="/<\/?[^>]+(>|$)/g"
                    class="file-selector__search"
                    @fieldvaluechanged="searchDocuments"
                    @enterpressed="searchDocuments" />
          <div class="file-selector__files-wrapper">
            <attached-files-list :files-list="allDocuments"
                                 :selected-files="selectedFiles"
                                 :is-single="isSingleSelect"
                                 @selected="catchSelection"
                                 @unselected="catchUnSelection" />
          </div>
        </div>
        <!----  Upload New Files ---->
        <template v-if="isUploadTab">
          <drag-drop-attachment id="fileSelectorUpload"
                                class="file-selector__upload"
                                :attachments="attachments"
                                :attachments-errors="attachmentsErrors"
                                :subtitle="subtitleText"
                                :is-single="isSingleSelect"
                                @attachmentchanged="catchAttachment"
                                @attachmentdeleted="catchDelete" />
        </template>
      </div>
      <div class="file-selector__actions">
        <button class="sk-btn sk-btn--white sk-btn--borderless file-selector__btn"
                @click.prevent.stop="closePopover">{{ $gettext('Cancel') }}</button>
        <button class="sk-btn sk-btn--default file-selector__btn"
                @click.prevent.stop="attachFiles">{{ $gettext('Attach') }}</button>
      </div>
    </div>
    <slot name="trigger">
    </slot>
  </section>
</template>

<script>
  import DragDropAttachment from '@/components/shared_components/DragDropAttachment';
  import AttachedFilesList from '@/components/documents/file_selector/AttachedFilesList';
  import constants from '@/modules/constants';
  import {mapGetters, mapMutations, mapState} from 'vuex';

  export default {
    components: {
      'drag-drop-attachment': DragDropAttachment,
      'attached-files-list': AttachedFilesList
    },
    props: {
      isSingleSelect: {
        type: Boolean,
        default: false
      },
      isOpen: {
        type: Boolean,
        default: false
      }
    },
    data() {
      return {
        searchKey: '',
        currentTab: 'documents',
        isPopoverVisible: false,
        progressActive: false
      };
    },
    computed: {
      ...mapGetters('DocumentCenterStore', [
        'attachments'
      ]),
      ...mapState('DocumentCenterStore', {
        selectedFiles: (state) => state.selectedFiles || [],
        allDocuments: (state) => state.allDocuments.documents || [],
        attachmentsErrors: (state) => state.errors.attachments.errors || []
      }),
      isDocumentsTab() { return this.currentTab === 'documents'; },
      isUploadTab() { return this.currentTab === 'upload'; },
      searchPlaceholder() { return this.$gettext('Search...'); },
      subtitleText() { return this.$gettext('All .docx, .xlsx, .jpg, .png and .pdf files up to 50 MB are supported.'); },
      allTabs() {
        return [
          {
            id: 'documents',
            title: this.$pgettext('translation', 'Documents'),
            onClick: this.setCurrentTab,
            condition: true
          },
          {
            id: 'upload',
            title: this.$pgettext('translation', 'Upload'),
            onClick: this.setCurrentTab,
            condition: true
          }
        ];
      },
      isDocumentsEmpty() { return this.allDocuments && !this.allDocuments.length; }
    },
    watch: {
      isOpen(newValue) {
        if (newValue === true && this.isDocumentsEmpty) this.fetchDocuments();
        if (newValue === true) this.clearSelectedFiles();
      },
    },
    methods: {
      ...mapMutations('DocumentCenterStore', [
        'setAttachments',
        'deleteAttachment',
        'setError',
        'removeErrors',
        'catchSelection',
        'catchUnSelection',
        'clearStore',
        'clearSelectedFiles',
      ]),
      catchAttachment(target) {
        // block multiple selection for single select
        if (this.isSingleSelect && this.attachments && this.attachments.length) return '';
        const dt = new DataTransfer();
        let fileList = target;
        let targetFiles = '';
        if (this.isSingleSelect) {
          dt.items.add(target.files[0]);
          targetFiles = dt.files;
          fileList = dt;
        } else targetFiles = target.files;
        this.removeErrors();
        if (this.validateFiles(fileList) && targetFiles && targetFiles.length) {
          this.setAttachments(fileList);
        }
      },
      validateFiles(dataTransfer) {
        const files = dataTransfer.files;
        let isValidForm = true;
        if (files && files.length) {
          const fileType = files[0].type;
          const fileSize = files[0].size;
          const allowedFileTypes = constants.DOCUMENT_CENTER_ALLOWED_FILE_TYPES;
          if (!allowedFileTypes.includes(fileType)) {
            isValidForm = false;
            this.setError({
              fieldName: 'attachments',
              errorText: this.$gettext('Invalid file type')
            });
          }
          if (fileSize > constants.DOCUMENT_CENTER_MAX_FILE_SIZE) {
            isValidForm = false;
            this.setError({
              fieldName: 'attachments',
              errorText: this.$gettext('File size can\'t be more than 20 megabytes.')
            });
          }
        }

        return isValidForm;
      },
      attachFiles() {
        if (this.isDocumentsTab) {
          this.$emit('attachexistingfiles', this.selectedFiles);
          this.closePopover();
        } else {
          const form = new FormData();
          for (const file of this.attachments) {
            form.append('document[file]', file.file);
          }
          this.progressActive = true;
          this.$store.dispatch('DocumentCenterStore/addDocument', form).then((newDocument) => {
            this.progressActive = false;
            this.$emit('attachnewfiles', newDocument);
            this.closePopover();
          }).catch(() => {
            this.progressActive = false;
          });
        }
      },
      catchDelete({index, id}) {
        this.deleteAttachment({index, id});
        this.removeErrors();
      },
      setCurrentTab(tab) {
        this.currentTab = tab;
      },
      closePopover() {
        this.clearStore();
        this.searchKey = '';
        this.$emit('close');
      },
      searchDocuments(value) {
        clearInterval(this.requestTimer);
        this.searchKey = value;
        this.requestTimer = setTimeout(() => {
          this.fetchDocuments();
        }, 500);
      },
      fetchDocuments() {
        const params = {
          items: '25'
        };
        if (this.searchKey) params['s[filename_search_value_cont]'] = this.searchKey;
        this.progressActive = true;
        this.$store.dispatch('DocumentCenterStore/getDocuments', params).then(() => {
          this.progressActive = false;
        }).catch(() => {
          this.progressActive = false;
        });
      }
    },
    mounted() {
      if (this.isDocumentsEmpty) this.fetchDocuments();
    },
  };
</script>

<style>
.file-selector__search .sk-input__field-icon {
  left: 10px;
}

.file-selector__search .sk-input__input--with-icon {
  padding-right: 10px;
  padding-left: 40px;
}
</style>

<style scoped>
.file-selector__content {
  margin-bottom: 10px;
  border: 1px solid #d3d5de;
  border-radius: 16px;
}

.file-selector__header {
  padding: 12px 12px 0 12px;
}

.file-selector__tabs {
  display: flex;
  margin-bottom: 12px;
  padding: 12px 12px 0 12px;
  border-bottom: 1px solid #d3d5de;
}

.file-selector__files-wrapper {
  overflow: scroll;
  min-height: 250px;
  max-height: 320px;
}

.file-selector__search-wrapper {
  padding: 4px;
}

.file-selector__upload {
  padding: 0 24px 24px 32px;
}

.file-selector__actions {
  display: flex;
  align-items: flex-end;
  justify-content: flex-end;
  width: 100%;
  padding: 18px 24px;
  border-top: 1px solid var(--cool-gray-200);
}

.file-selector__btn {
  width: auto;
  margin: 0 5px 0;
  padding: 0 20px;
}
</style>
